import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import { Close, ChevronRight, ChevronLeft } from '@material-ui/icons';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogPaper: {
      borderRadius: 8,
      maxWidth: 560,
    },
    header: {
      padding: '24px 24px 0 24px',
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: 16,
      right: 16,
      color: theme.palette.text.secondary,
    },
    content: {
      padding: '24px',
    },
    progressIndicator: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 24,
    },
    progressDot: {
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: theme.palette.grey[300],
      margin: '0 6px',
    },
    progressDotActive: {
      backgroundColor: '#796fff',
    },
    progressDotComplete: {
      backgroundColor: '#796fff',
    },
    questionLabel: {
      fontWeight: 600,
      fontSize: 16,
      color: theme.palette.text.primary,
      marginBottom: 8,
    },
    questionHint: {
      fontSize: 14,
      color: theme.palette.text.secondary,
      marginBottom: 16,
    },
    optionLabel: {
      fontSize: 14,
    },
    textField: {
      marginTop: 8,
    },
    actions: {
      padding: '16px 24px 24px 24px',
      justifyContent: 'space-between',
    },
    secondaryButton: {
      color: theme.palette.text.secondary,
      textTransform: 'none',
      fontWeight: 500,
      fontSize: 14,
    },
    primaryButton: {
      backgroundColor: '#796fff',
      color: '#ffffff',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: 14,
      padding: '8px 20px',
      borderRadius: 6,
      '&:hover': {
        backgroundColor: '#635ac7',
      },
      '&:disabled': {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.grey[500],
      },
    },
  })
);

interface QuestionnaireDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: (answers: QuestionnaireAnswers) => void;
  initialAnswers?: QuestionnaireAnswers;
  isSubmitting?: boolean;
}

export interface QuestionnaireAnswers {
  orgDescription: string;
  volunteeringStructure: string[];
  reportingRequirements: string[];
  rosteringApproach: string;
  attendanceTracking: string;
}

const QUESTIONS = [
  {
    id: 'orgDescription',
    label: 'Briefly describe what your organisation does',
    hint: 'This helps us tailor suggestions to your specific needs.',
    type: 'text' as const,
  },
  {
    id: 'volunteeringStructure',
    label: 'How do you organise your volunteering?',
    hint: 'Select all that apply.',
    type: 'multiselect' as const,
    options: [
      { value: 'program', label: 'By program area (e.g., Youth Support, Community Meals)' },
      { value: 'location', label: 'By location (e.g., North Site, Main Office)' },
      { value: 'role', label: 'By volunteer role (e.g., Drivers, Mentors)' },
      { value: 'none', label: "We don't have specific groupings yet" },
    ],
  },
  {
    id: 'reportingRequirements',
    label: 'Who do you report volunteer activities to?',
    hint: 'Select all that apply.',
    type: 'multiselect' as const,
    options: [
      { value: 'funders', label: 'Funders / Government' },
      { value: 'board', label: 'Board / Committee' },
      { value: 'public', label: 'Public / Annual reports' },
      { value: 'volunteers', label: 'Volunteers themselves' },
      { value: 'internal', label: 'Internal management only' },
      { value: 'none', label: 'No reporting needed' },
    ],
  },
  {
    id: 'rosteringApproach',
    label: 'How do volunteers sign up for shifts?',
    hint: 'This helps us configure the right rostering settings.',
    type: 'radio' as const,
    options: [
      { value: 'manager', label: 'We roster them (manager assigns)' },
      { value: 'selfservice', label: 'Self-service (volunteers pick available shifts)' },
      { value: 'dropin', label: 'Drop-in (volunteers report after attending, no roster)' },
      { value: 'mix', label: 'Mix of the above' },
      { value: 'unsure', label: 'Not sure yet' },
    ],
  },
  {
    id: 'attendanceTracking',
    label: 'How do you want to collect volunteer attendance?',
    hint: 'This affects how sessions track volunteer participation.',
    type: 'radio' as const,
    options: [
      { value: 'cico', label: 'Check In & Check Out (Individual Times for Attendees)' },
      { value: 'attendance', label: 'Attendance (One start and end for all attendees)' },
      { value: 'none', label: 'No attendance tracking' },
    ],
  },
];

const defaultAnswers: QuestionnaireAnswers = {
  orgDescription: '',
  volunteeringStructure: [],
  reportingRequirements: [],
  rosteringApproach: '',
  attendanceTracking: '',
};

export function QuestionnaireDialog({
  open,
  onClose,
  onComplete,
  initialAnswers,
  isSubmitting = false,
}: QuestionnaireDialogProps) {
  const classes = useStyles();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<QuestionnaireAnswers>(initialAnswers ?? defaultAnswers);

  const currentQuestion = QUESTIONS[currentStep];
  const isLastStep = currentStep === QUESTIONS.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      onComplete(answers);
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleTextChange = (value: string) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleRadioChange = (value: string) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleMultiselectChange = (fieldId: string, value: string, checked: boolean) => {
    setAnswers((prev) => {
      const current = prev[fieldId as keyof QuestionnaireAnswers] as string[];
      if (checked) {
        return { ...prev, [fieldId]: [...current, value] };
      }
      return { ...prev, [fieldId]: current.filter((v) => v !== value) };
    });
  };

  const isCurrentStepValid = () => {
    const answer = answers[currentQuestion.id as keyof QuestionnaireAnswers];
    if (currentQuestion.type === 'text') {
      return (answer as string).trim().length > 0;
    }
    if (currentQuestion.type === 'multiselect') {
      return (answer as string[]).length > 0;
    }
    return (answer as string).length > 0;
  };

  const renderQuestion = () => {
    switch (currentQuestion.type) {
      case 'text':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            variant='outlined'
            placeholder='e.g., We are a community food bank that provides meals and groceries to families in need...'
            value={answers.orgDescription}
            onChange={(e) => handleTextChange(e.target.value)}
            className={classes.textField}
          />
        );
      case 'multiselect': {
        const currentValues = answers[currentQuestion.id as keyof QuestionnaireAnswers] as string[];
        return (
          <Box>
            {currentQuestion.options?.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={currentValues.includes(option.value)}
                    onChange={(e) => handleMultiselectChange(currentQuestion.id, option.value, e.target.checked)}
                    color='primary'
                  />
                }
                label={<span className={classes.optionLabel}>{option.label}</span>}
                style={{ display: 'block', marginBottom: 8 }}
              />
            ))}
          </Box>
        );
      }
      case 'radio':
        return (
          <FormControl component='fieldset'>
            <RadioGroup
              value={answers[currentQuestion.id as keyof QuestionnaireAnswers] as string}
              onChange={(e) => handleRadioChange(e.target.value)}
            >
              {currentQuestion.options?.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio color='primary' />}
                  label={<span className={classes.optionLabel}>{option.label}</span>}
                  style={{ marginBottom: 8 }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth classes={{ paper: classes.dialogPaper }}>
      <Box className={classes.header}>
        <IconButton className={classes.closeButton} onClick={onClose} size='small'>
          <Close fontSize='small' />
        </IconButton>
        <Typography
          style={{
            fontWeight: 700,
            fontSize: 20,
            letterSpacing: '-0.02em',
            color: '#343434',
          }}
        >
          Quick Questions
        </Typography>
        <Typography style={{ color: '#545454', marginTop: 4, fontSize: 14 }}>
          Help us understand your organisation
        </Typography>
      </Box>

      <DialogContent className={classes.content}>
        {/* Progress dots */}
        <Box className={classes.progressIndicator}>
          {QUESTIONS.map((question, index) => (
            <Box
              key={question.id}
              className={`${classes.progressDot} ${
                index === currentStep
                  ? classes.progressDotActive
                  : index < currentStep
                  ? classes.progressDotComplete
                  : ''
              }`}
            />
          ))}
        </Box>

        {/* Question */}
        <FormLabel component='legend' className={classes.questionLabel}>
          {currentQuestion.label}
        </FormLabel>
        <Typography className={classes.questionHint}>{currentQuestion.hint}</Typography>

        {renderQuestion()}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          className={classes.secondaryButton}
          onClick={isFirstStep ? onClose : handleBack}
          startIcon={!isFirstStep ? <ChevronLeft /> : undefined}
        >
          {isFirstStep ? 'Cancel' : 'Back'}
        </Button>
        <Button
          className={classes.primaryButton}
          onClick={handleNext}
          disabled={!isCurrentStepValid() || isSubmitting}
          endIcon={
            isSubmitting ? <CircularProgress size={16} color='inherit' /> : isLastStep ? undefined : <ChevronRight />
          }
        >
          {isLastStep ? (isSubmitting ? 'Saving...' : 'Complete') : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
